export type Bi = { fr: string; en: string }

export const trustSolutionCopy = {
  eyebrow: { fr: 'La solution', en: 'The solution' },
  headline: [
    { text: { fr: 'ATNA ne devine pas.', en: 'ATNA doesn’t guess.' }, accent: false },
    { text: { fr: 'Elle vérifie avant d’agir.', en: 'It verifies before acting.' }, accent: true },
  ],
  body: {
    fr: 'Chaque réponse passe par une chaîne de confiance qui relie l’identité, le contexte autorisé, les sources et les règles de votre organisation avant de recommander ou d’exécuter une action.',
    en: 'Every answer goes through a chain of trust that connects identity, authorized context, and your organization’s sources and rules before recommending or executing an action.',
  },
}

export type SystemNodeKey = 'sources' | 'rules' | 'data' | 'systems' | 'oversight' | 'audit'

export const systemInputs: { key: SystemNodeKey; label: Bi }[] = [
  { key: 'sources', label: { fr: 'Sources officielles', en: 'Official sources' } },
  { key: 'rules', label: { fr: 'Règles et politiques', en: 'Rules and policies' } },
  { key: 'data', label: { fr: 'Données de confiance', en: 'Trusted data' } },
]
export const systemGovernance: { key: SystemNodeKey; label: Bi }[] = [
  { key: 'systems', label: { fr: 'Systèmes connectés', en: 'Connected systems' } },
  { key: 'oversight', label: { fr: 'Supervision humaine', en: 'Human oversight' } },
  { key: 'audit', label: { fr: 'Audit et traçabilité', en: 'Audit and traceability' } },
]
export const atnaVerbs: Bi[] = [
  { fr: 'Comprend', en: 'Understands' },
  { fr: 'Vérifie', en: 'Verifies' },
  { fr: 'Recommande', en: 'Recommends' },
  { fr: 'Orchestre', en: 'Orchestrates' },
]
export const atnaWithTrust: Bi = { fr: 'En toute confiance', en: 'With confidence' }

export type StageIcon = 'identity' | 'context' | 'verify' | 'evaluate' | 'recommend' | 'act'
export const trustStages: { icon: StageIcon; verb: Bi; title: Bi; question: Bi }[] = [
  { icon: 'identity', verb: { fr: 'Identifie', en: 'Identifies' }, title: { fr: 'Identité vérifiée', en: 'Verified identity' }, question: { fr: 'Qui demande ?', en: 'Who is asking?' } },
  { icon: 'context', verb: { fr: 'Contextualise', en: 'Contextualizes' }, title: { fr: 'Contexte autorisé', en: 'Authorized context' }, question: { fr: 'Quelle situation ?', en: 'What situation?' } },
  { icon: 'verify', verb: { fr: 'Vérifie', en: 'Verifies' }, title: { fr: 'Sources + règles', en: 'Sources + rules' }, question: { fr: 'Que savons-nous réellement ?', en: 'What do we actually know?' } },
  { icon: 'evaluate', verb: { fr: 'Évalue', en: 'Evaluates' }, title: { fr: 'TAME', en: 'TAME' }, question: { fr: 'Niveau de confiance', en: 'Confidence level' } },
  { icon: 'recommend', verb: { fr: 'Recommande', en: 'Recommends' }, title: { fr: 'Réponse explicable', en: 'Explainable answer' }, question: { fr: 'Pourquoi cette réponse ?', en: 'Why this answer?' } },
  { icon: 'act', verb: { fr: 'Agit', en: 'Acts' }, title: { fr: 'Action autorisée', en: 'Authorized action' }, question: { fr: 'Exécuter si c’est autorisé', en: 'Execute when permitted' } },
]

/* Illustrative demo example — the 0,92 confidence value is demo UI data, not measured ATNA performance. */
export const trustExample = {
  demoNote: 'DEMO / ILLUSTRATIVE DATA ONLY',
  question: {
    label: { fr: 'Question d’un usager', en: 'A user’s question' },
    text: {
      fr: 'Où puis-je obtenir mon permis de rénovation pour ma propriété ?',
      en: 'Where can I get my renovation permit for my property?',
    },
    time: { fr: '10 h 24', en: '10:24' },
  },
  recommendation: {
    label: { fr: 'Recommandation vérifiée', en: 'Verified recommendation' },
    confidence: { fr: 'Confiance élevée', en: 'High confidence' },
    score: { fr: '0,92', en: '0.92' },
    checks: [
      { fr: 'Source officielle', en: 'Official source' },
      { fr: 'Règlement applicable', en: 'Applicable by-law' },
      { fr: 'Information à jour', en: 'Up-to-date information' },
      { fr: 'Contexte autorisé', en: 'Authorized context' },
    ],
    answer: {
      fr: 'Vous pouvez soumettre une demande de permis de rénovation via le portail citoyen. Voici les documents requis.',
      en: 'You can submit a renovation permit application through the resident portal. Here are the required documents.',
    },
    controls: [
      { key: 'answer', label: { fr: 'Réponse', en: 'Answer' } },
      { key: 'sources', label: { fr: 'Sources (3)', en: 'Sources (3)' } },
      { key: 'why', label: { fr: 'Pourquoi ?', en: 'Why?' } },
    ],
  },
  action: {
    label: { fr: 'Prochaine action', en: 'Next action' },
    title: { fr: 'Préparer ma demande de permis', en: 'Prepare my permit application' },
    text: { fr: 'Accéder au formulaire prérempli avec vos informations.', en: 'Open the form prefilled with your information.' },
    button: { fr: 'Commencer la demande', en: 'Start the application' },
  },
  trace: { fr: 'Chaque étape laisse une trace.', en: 'Every step leaves a trace.' },
  traceChain: {
    fr: 'Source → règle → contexte → confiance → recommandation → autorisation → action',
    en: 'Source → rule → context → confidence → recommendation → authorization → action',
  },
}

export type CommitmentIcon = 'shield' | 'doc' | 'users' | 'user'
export const trustCommitments = {
  eyebrow: { fr: 'Nos engagements pour une intelligence de confiance', en: 'Our commitments to trusted intelligence' },
  items: [
    { icon: 'shield', title: { fr: 'Vérifier avant d’affirmer', en: 'Verify before asserting' }, text: { fr: 'Nous nous appuyons sur des sources fiables et des règles claires.', en: 'We draw on reliable sources and clear rules.' } },
    { icon: 'doc', title: { fr: 'Expliquer quand c’est important', en: 'Explain when it matters' }, text: { fr: 'Nous rendons nos réponses compréhensibles et traçables.', en: 'We make our answers understandable and traceable.' } },
    { icon: 'users', title: { fr: 'Demander avant d’agir', en: 'Ask before acting' }, text: { fr: 'Nous respectons les autorisations et les préférences.', en: 'We respect authorizations and preferences.' } },
    { icon: 'user', title: { fr: 'Garder l’humain aux commandes', en: 'Keep humans in control' }, text: { fr: 'Nous soutenons la prise de décision et la supervision humaines.', en: 'We support human decision-making and oversight.' } },
  ] as { icon: CommitmentIcon; title: Bi; text: Bi }[],
}

export const trustFinalCta = {
  eyebrow: { fr: 'BCITI | ATNA — Intelligence de confiance', en: 'BCITI | ATNA — Trusted intelligence' },
  headline: {
    before: { fr: 'Et si vous pouviez ', en: 'What if you could ' },
    accent: { fr: 'faire confiance', en: 'trust' },
    after: { fr: 'à l’intelligence que vous déployez ?', en: 'the intelligence you deploy?' },
  },
  body: {
    fr: 'Découvrez comment BCITI | ATNA peut intégrer une intelligence vérifiée, gouvernée et traçable à vos services et à vos opérations.',
    en: 'Discover how BCITI | ATNA can bring verified, governed and traceable intelligence into your services and operations.',
  },
  cta: { fr: 'Demander une démo', en: 'Request a demo' },
  signature: { fr: 'Comprendre. Vérifier. Recommander. Agir — avec confiance.', en: 'Understand. Verify. Recommend. Act — with confidence.' },
}
